import { normalizeSignature } from "@gearbox-protocol/sdk/permissionless";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useAccount,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { RecoveryMessageInfo } from "@/core/recovery";
import { useWeb3 } from "./use-web3";

export function useExecuteRecovery({
  recovery,
  updateRecoveries,
}: {
  recovery: RecoveryMessageInfo;
  updateRecoveries: (recovery: RecoveryMessageInfo) => void;
}) {
  const { address, chainId } = useAccount();
  const publicClient = usePublicClient({ chainId });

  const { crossChainMultisig } = useWeb3();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!address || !publicClient) return;
      if (chainId !== recovery.chainId) {
        await switchChainAsync({ chainId: recovery.chainId });
      }

      if (!crossChainMultisig) return;

      const signatures = recovery.signatures.map(s =>
        normalizeSignature(s.signature),
      );

      const hash = await writeContractAsync({
        address: crossChainMultisig.address,
        abi: crossChainMultisig.abi,
        functionName: "enableRecoveryMode",
        args: [
          {
            chainId: BigInt(recovery.chainId),
            startingBatchHash: recovery.startingBatchHash,
            signatures,
          },
        ],
      });

      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (receipt.status !== "success") {
        throw new Error(`Transaction failed: ${hash}`);
      }

      const block = await publicClient.getBlock({
        blockNumber: receipt.blockNumber,
      });

      updateRecoveries({
        ...recovery,
        transactionHash: hash,
        blockNumber: Number(receipt.blockNumber),
        timestamp: Number(block.timestamp),
      });
    },
    onSuccess: () => {
      toast.success("Recovery executed");
    },
    onError: error => {
      toast.error("Failed to execute recovery", {
        description: error.message,
      });
    },
  });

  return {
    execute: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error as Error | null,
  };
}
